import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useRuntimeConfig } from "../config/RuntimeConfigContext.jsx";
import { getPublicInviteCard, submitWorkerApplicationWithDocument } from "./publicInviteApi.js";
import {
  LANGUAGES,
  SKILL_CODES,
  BACKEND_KNOWN_SKILL_CODES,
  SKILL_ENGLISH_LABELS,
  getPublicInviteText,
  inferDefaultLanguage,
} from "./publicInviteTranslations.js";
import "./publicInviteRegister.css";

const NATIONALITY_OPTIONS = ["Bangladesh", "Laos", "Myanmar", "Cambodia", "Other"];
const SKILL_OTHER_CODE = "Other";

// Prompt 139A/139B: the backend's document-submit route only recognizes
// image/jpeg, image/png, image/webp (real magic-number sniffing, not just a
// Content-Type rubber stamp - see LocalPassportDocumentStorage.cs). PDF was
// accepted by the old Prompt 138V local-only picker's accept attribute but
// would be rejected by the real upload now, so it is dropped here.
const PASSPORT_ACCEPT = "image/jpeg,image/png,image/webp";
const PASSPORT_MAX_SIZE_BYTES = 8 * 1024 * 1024;

/**
 * Prompt 139B: maps a UI-selected skill code to what actually gets
 * submitted. The backend (Prompt 139A, PrimarySkills.cs) only recognizes 5
 * codes as first-class values - ConstructionWorker/Mason/Welder/
 * FactoryWorker/Other - a deliberately closed set. This UI offers 10
 * options (the product requirement); the 5 codes the backend does not know
 * (Electrician/Carpenter/Cleaner/Driver/GeneralWorker) are submitted as
 * PrimarySkillCode="Other" with PrimarySkillOtherText set to that skill's
 * English display name - the backend's own existing free-text path for
 * "Other", not a code it would reject. This is a real, honestly persisted
 * value (Agent/SuperAdmin still see the correct skill name via
 * WorkerApplicationService's Skill.Name), never data silently dropped or
 * faked - just not (yet) its own dedicated backend code.
 */
function resolveSkillSubmission(code, otherText) {
  if (code === SKILL_OTHER_CODE) {
    return { code: SKILL_OTHER_CODE, otherText: otherText.trim() };
  }
  if (BACKEND_KNOWN_SKILL_CODES.includes(code)) {
    return { code, otherText: null };
  }
  return { code: SKILL_OTHER_CODE, otherText: SKILL_ENGLISH_LABELS[code] ?? code };
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Object-URL thumbnail for the selected passport file - degrades to no thumbnail (never an error) if URL.createObjectURL isn't available. */
function usePreviewUrl(file) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!file || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
      setUrl(null);
      return undefined;
    }

    let objectUrl;
    try {
      objectUrl = URL.createObjectURL(file);
    } catch {
      setUrl(null);
      return undefined;
    }

    setUrl(objectUrl);
    return () => {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        // Nothing to do - the browser will reclaim it on navigation anyway.
      }
    };
  }, [file]);

  return url;
}

/**
 * Prompt 139B: true only when the browser exposes a real getUserMedia and
 * the page is running in a secure context (HTTPS or localhost) - both are
 * required for a real camera stream to ever succeed. Computed fresh on
 * every render (cheap) rather than memoized once, so a test can stub
 * navigator.mediaDevices/window.isSecureContext before rendering and see it
 * take effect immediately.
 */
function isCameraCaptureSupported() {
  if (typeof navigator === "undefined" || !navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== "function") {
    return false;
  }
  if (typeof window !== "undefined" && window.isSecureContext === false) {
    return false;
  }
  return true;
}

// Prompt 138V: the envelope's own closing/opening transition (see
// EnvelopePanel/handleOpenEnvelope below) is purely a visual flourish - the
// actual view switch always happens on a timer this short, and is skipped
// entirely under prefers-reduced-motion, so nothing about correctness or
// test determinism depends on this exact number beyond "short".
const ENVELOPE_OPEN_ANIMATION_MS = 360;

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function getInitials(name) {
  return (name || "")
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/**
 * Tracks the OS/browser-level `prefers-reduced-motion` setting so the
 * envelope-opening animation (Prompt 138V requirement B) can be skipped
 * outright while landing on the exact same view states either way. Guards
 * every window.matchMedia access - jsdom (this file's own test
 * environment) does not implement matchMedia by default, and this must
 * never throw or leave the page stuck.
 */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    let mql;
    try {
      mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    } catch {
      return undefined;
    }
    const handleChange = (event) => setReduced(event.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handleChange);
      return () => mql.removeEventListener("change", handleChange);
    }
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, []);

  return reduced;
}

/**
 * /register?invite={inviteCode} - the public, unauthenticated worker-facing
 * web page opened from an Agent's Invite Card (Prompt 092/W39 reference,
 * restructured by Prompt 133, extended by Prompt 138V). Deliberately
 * rendered outside RouteGuard/AppShell/Sidebar in App.jsx: this is not a
 * backoffice screen and must never require a session token. All API access
 * goes through publicInviteApi.js, which never attaches an Authorization
 * header even if one exists in this browser tab.
 *
 * Prompt 133: a valid, available invite shows the Invite Letter before the
 * Quick Register form - only clicking the letter's own "เริ่ม Quick
 * Register" CTA switches to the form.
 *
 * Prompt 138V adds a third `view` in front of the letter: `"envelope"`, a
 * closed-envelope first screen (requirement B) that must be opened before
 * the letter is shown at all - `view` is now `"envelope" | "letter" |
 * "form"`, always starting at `"envelope"` for an available card. It also
 * makes every visible string on this page language-aware
 * (publicInviteTranslations.js, requirement A).
 *
 * Prompt 139B replaces Prompt 138V's local-only passport picker with a real
 * one: skill, passport number, and the passport photo are now all required
 * fields, submitted together via multipart/form-data to the real Prompt
 * 139A document-submit route (submitWorkerApplicationWithDocument) - never
 * the plain JSON submitWorkerApplication route, since that route has no
 * skill/passport/document fields at all and this form now always collects
 * them.
 */
export default function PublicInviteRegisterPage() {
  const config = useRuntimeConfig();
  const [searchParams] = useSearchParams();
  const inviteCode = (searchParams.get("invite") || "").trim();
  const prefersReducedMotion = usePrefersReducedMotion();

  const [lookup, setLookup] = useState({ status: inviteCode ? "loading" : "missing-invite", data: null, error: null });
  const [view, setView] = useState("envelope"); // "envelope" | "letter" | "form" - only meaningful once the card is available and not yet submitted
  const [envelopeOpening, setEnvelopeOpening] = useState(false);
  const envelopeTimerRef = useRef(null);
  const [language, setLanguageState] = useState("th");
  const languageTouchedRef = useRef(false);
  const [fullNameNative, setFullNameNative] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [nationality, setNationality] = useState(NATIONALITY_OPTIONS[0]);
  const [primarySkillCode, setPrimarySkillCode] = useState("");
  const [primarySkillOtherText, setPrimarySkillOtherText] = useState("");
  const [passportNumber, setPassportNumber] = useState("");
  const [passportFile, setPassportFile] = useState(null);
  const [formErrors, setFormErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successResponse, setSuccessResponse] = useState(null);
  const [submittedSummary, setSubmittedSummary] = useState(null);

  const t = getPublicInviteText(language);

  const handleLanguageChange = useCallback((code) => {
    languageTouchedRef.current = true;
    setLanguageState(code);
  }, []);

  useEffect(() => {
    if (!inviteCode) {
      setLookup({ status: "missing-invite", data: null, error: null });
      return undefined;
    }

    let cancelled = false;
    setLookup({ status: "loading", data: null, error: null });

    getPublicInviteCard(inviteCode)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setLookup({ status: "ready", data, error: null });
        // Requirement A: default to the invite's own intended language
        // (e.g. Bengali for a BD-targeted invite) only if the worker has
        // not already picked a language chip themselves.
        if (!languageTouchedRef.current) {
          const inferred = inferDefaultLanguage(data);
          if (inferred) {
            setLanguageState(inferred);
          }
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setLookup({ status: "error", data: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [inviteCode]);

  useEffect(
    () => () => {
      if (envelopeTimerRef.current) {
        window.clearTimeout(envelopeTimerRef.current);
      }
    },
    []
  );

  function handleOpenEnvelope() {
    if (prefersReducedMotion) {
      setView("letter");
      return;
    }
    setEnvelopeOpening(true);
    envelopeTimerRef.current = window.setTimeout(() => {
      setView("letter");
    }, ENVELOPE_OPEN_ANIMATION_MS);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const errors = [];
    if (!fullNameNative.trim()) {
      errors.push(t.errorFullNameRequired);
    }
    if (!phoneNumber.trim()) {
      errors.push(t.errorPhoneRequired);
    }
    if (!primarySkillCode) {
      errors.push(t.errorSkillRequired);
    } else if (primarySkillCode === SKILL_OTHER_CODE && !primarySkillOtherText.trim()) {
      errors.push(t.errorSkillOtherRequired);
    }
    const trimmedPassportNumber = passportNumber.trim();
    if (!trimmedPassportNumber) {
      errors.push(t.errorPassportNumberRequired);
    } else if (trimmedPassportNumber.length < 5) {
      errors.push(t.errorPassportNumberTooShort);
    }
    // Prompt 139A's document-submit route requires a real passport document
    // on every request (PublicInviteService.SubmitWorkerApplicationWithDocumentAsync
    // rejects a missing one as a validation error) - so this form enforces
    // the same rule, rather than letting a worker submit and only then
    // learn the backend rejected it.
    if (!passportFile) {
      errors.push(t.errorPassportDocumentRequired);
    } else if (passportFile.size > PASSPORT_MAX_SIZE_BYTES) {
      // Client-side mirror of LocalPassportDocumentStorage.MaxSizeBytes -
      // catches an obviously oversized file before a round trip, the
      // backend's own check (real, not just this UI hint) still applies too.
      errors.push(t.errorPassportDocumentTooLarge);
    }
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors([]);
    setSubmitError(null);
    setSubmitting(true);
    try {
      const skill = resolveSkillSubmission(primarySkillCode, primarySkillOtherText);

      const formData = new FormData();
      formData.append("InviteCode", inviteCode);
      formData.append("FullNameNative", fullNameNative.trim());
      formData.append("Nationality", nationality);
      formData.append("PhoneNumber", phoneNumber.trim());
      formData.append("PreferredLanguageCode", language);
      formData.append("PrimarySkillCode", skill.code);
      if (skill.otherText) {
        formData.append("PrimarySkillOtherText", skill.otherText);
      }
      formData.append("PassportNumber", trimmedPassportNumber);
      formData.append("PassportDocument", passportFile, passportFile.name);

      const response = await submitWorkerApplicationWithDocument(formData);
      setSuccessResponse(response);
      // Snapshot taken at the moment of THIS successful submit - see
      // SuccessPanel's own doc comment for why this is real, not fake data.
      setSubmittedSummary({ hasSkill: true, hasPassportNumber: true, hasDocument: Boolean(passportFile) });
    } catch (error) {
      setSubmitError(error?.message || t.submitGenericError);
    } finally {
      setSubmitting(false);
    }
  }

  const publicBaseDisplay = (config.publicInviteBaseUrl || "").replace(/^https?:\/\//, "");
  const urlPillText = inviteCode ? `${publicBaseDisplay}?invite=${inviteCode}` : publicBaseDisplay;
  const card = lookup.status === "ready" ? lookup.data : null;
  const primaryPhoneContact = card?.agentContacts?.find((c) => c.contactType === "Phone") ?? card?.agentContacts?.[0] ?? null;

  return (
    <div className="wx-pr-root">
      <Topbar
        t={t}
        urlPillText={urlPillText}
        language={language}
        onLanguageChange={handleLanguageChange}
        callHref={primaryPhoneContact ? `tel:${primaryPhoneContact.contactValue}` : null}
      />

      {lookup.status === "missing-invite" && <CenteredState title={t.missingInviteTitle} body={t.missingInviteBody} />}

      {lookup.status === "loading" && (
        <div className="wx-pr-center-wrap">
          <div className="wx-pr-spinner" aria-hidden="true" />
          <p className="wx-pr-loading-text">{t.loadingText}</p>
        </div>
      )}

      {lookup.status === "error" && (
        <CenteredState
          warning
          title={lookup.error?.status === 404 ? t.errorTitleNotFound : t.errorTitleGeneric}
          body={lookup.error?.message || (lookup.error?.status === 404 ? t.errorBodyNotFound : t.errorBodyGeneric)}
        />
      )}

      {card && !card.available && (
        <CenteredState
          warning
          title={t.unavailableTitle}
          body={t.unavailableReasons[card.unavailableReason] || t.unavailableDefault}
        >
          <AgentSummaryCompact card={card} t={t} />
        </CenteredState>
      )}

      {card && card.available && successResponse && (
        <div className="wx-pr-center-wrap">
          <SuccessPanel response={successResponse} submittedSummary={submittedSummary} t={t} />
        </div>
      )}

      {card && card.available && !successResponse && view === "envelope" && (
        <main className="wx-pr-envelope-screen">
          <EnvelopePanel card={card} t={t} opening={envelopeOpening} onOpen={handleOpenEnvelope} />
        </main>
      )}

      {card && card.available && !successResponse && view === "letter" && (
        <main className="wx-pr-letter-screen">
          <LetterPanel card={card} t={t} onStartQuickRegister={() => setView("form")} />
        </main>
      )}

      {card && card.available && !successResponse && view === "form" && (
        <main className="wx-pr-form-screen">
          <FormSummaryBar card={card} t={t} onBack={() => setView("letter")} />
          <FormPanel
            card={card}
            t={t}
            fullNameNative={fullNameNative}
            phoneNumber={phoneNumber}
            nationality={nationality}
            primarySkillCode={primarySkillCode}
            primarySkillOtherText={primarySkillOtherText}
            passportNumber={passportNumber}
            passportFile={passportFile}
            onFullNameNativeChange={setFullNameNative}
            onPhoneNumberChange={setPhoneNumber}
            onNationalityChange={setNationality}
            onPrimarySkillCodeChange={setPrimarySkillCode}
            onPrimarySkillOtherTextChange={setPrimarySkillOtherText}
            onPassportNumberChange={setPassportNumber}
            onPassportFileChange={setPassportFile}
            formErrors={formErrors}
            submitting={submitting}
            submitError={submitError}
            onSubmit={handleSubmit}
            callHref={primaryPhoneContact ? `tel:${primaryPhoneContact.contactValue}` : null}
          />
        </main>
      )}
    </div>
  );
}

function Topbar({ t, urlPillText, language, onLanguageChange, callHref }) {
  return (
    <header className="wx-pr-topbar">
      <div className="wx-pr-brand">
        <div className="wx-pr-brand-mark" aria-hidden="true">
          WX
        </div>
        <div className="wx-pr-brand-text">
          <strong>Workmate X</strong>
          <span>{t.brandSubtitle}</span>
        </div>
      </div>

      {urlPillText && (
        <div className="wx-pr-url-pill">
          <span className="wx-pr-url-pill-lock" aria-hidden="true">
            <CheckIcon />
          </span>
          <span className="wx-pr-url-pill-text">{urlPillText}</span>
        </div>
      )}

      <div className="wx-pr-top-actions">
        {LANGUAGES.map((chip) => (
          <button
            key={chip.code}
            type="button"
            className={`wx-pr-lang-chip${language === chip.code ? " wx-pr-lang-chip--active" : ""}`}
            aria-pressed={language === chip.code}
            onClick={() => onLanguageChange(chip.code)}
          >
            {chip.label}
          </button>
        ))}
        {callHref && (
          <a className="wx-pr-call-link" href={callHref}>
            {t.callAgent}
          </a>
        )}
      </div>
    </header>
  );
}

function CenteredState({ title, body, warning, children }) {
  return (
    <div className="wx-pr-center-wrap">
      <div className={`wx-pr-state-card${warning ? " wx-pr-state-card--warning" : ""}`} role={warning ? "alert" : undefined}>
        <h1 className="wx-pr-state-title">{title}</h1>
        <p className="wx-pr-state-body">{body}</p>
        {children}
      </div>
    </div>
  );
}

function AgentSummaryCompact({ card, t }) {
  return (
    <div className="wx-pr-agent-contacts">
      <div className="wx-pr-contact-row">
        <span>Agent</span>
        <strong>
          {card.agentDisplayName} · {card.agentCode}
        </strong>
      </div>
      {card.agentContacts.map((contact, index) => (
        <div className="wx-pr-contact-row" key={`${contact.contactType}-${index}`}>
          <span>{t.contactTypeLabels[contact.contactType] ?? contact.contactType}</span>
          <strong>{contact.contactValue}</strong>
        </div>
      ))}
    </div>
  );
}

/**
 * Prompt 138V: the first screen for any available invite - a closed
 * envelope, never the letter itself. `onOpen` (handleOpenEnvelope above)
 * plays a short CSS opening animation (skipped outright under
 * prefers-reduced-motion) before the parent switches `view` to "letter".
 */
function EnvelopePanel({ card, t, opening, onOpen }) {
  const initials = getInitials(card.agentDisplayName);

  return (
    <section className={`wx-pr-envelope-card${opening ? " wx-pr-envelope-card--opening" : ""}`}>
      <div className="wx-pr-envelope-visual" aria-hidden="true">
        <div className="wx-pr-envelope-flap" />
        <div className="wx-pr-envelope-body">
          <div className="wx-pr-envelope-seal">{initials || "WX"}</div>
        </div>
      </div>
      <span className="wx-pr-envelope-eyebrow">{t.envelopeEyebrow}</span>
      <p className="wx-pr-envelope-from">{t.envelopeFromAgent(card.agentDisplayName)}</p>
      <p className="wx-pr-envelope-hint">{t.envelopeHint}</p>
      <button type="button" className="wx-pr-submit-button wx-pr-envelope-cta" onClick={onOpen}>
        {t.envelopeOpenCta}
      </button>
    </section>
  );
}

/**
 * Prompt 133/138V: the Invite Letter itself - shown only after the
 * envelope has been opened. Shows the Agent/line/intended-target info from
 * the real lookup response plus the required explanation lines and the
 * Quick Register CTA. Never renders the Quick Register form - FormPanel is
 * a separate component only mounted once `view === "form"`.
 */
function LetterPanel({ card, t, onStartQuickRegister }) {
  const initials = getInitials(card.agentDisplayName);
  const targetNote = [card.intendedCountryCode, card.intendedLanguageCode].filter(Boolean).join(" / ");

  return (
    <section className="wx-pr-letter-panel">
      <div className="wx-pr-letter-head">
        <span className="wx-pr-letter-eyebrow">{t.envelopeEyebrow}</span>
        <h1 className="wx-pr-letter-title">{t.letterTitle(card.agentDisplayName)}</h1>
        <p className="wx-pr-letter-desc">{t.letterDesc(card.appName)}</p>
      </div>

      <div className="wx-pr-letter-explain">
        <ul>
          {t.explainLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>

      <div className="wx-pr-letter-body">
        <aside className="wx-pr-agent-card">
          <div className="wx-pr-agent-row">
            <div className="wx-pr-agent-photo" aria-hidden="true">
              {initials || "WX"}
            </div>
            <div>
              <h2 className="wx-pr-agent-name">{card.agentDisplayName}</h2>
              <p className="wx-pr-agent-code">{card.agentCode}</p>
            </div>
          </div>
          <div className="wx-pr-agent-contacts">
            {card.agentContacts.length === 0 ? (
              <div className="wx-pr-contact-row">
                <span>{t.contactFallbackLabel}</span>
                <strong>{t.contactFallbackValue}</strong>
              </div>
            ) : (
              card.agentContacts.map((contact, index) => (
                <div className="wx-pr-contact-row" key={`${contact.contactType}-${index}`}>
                  <span>{t.contactTypeLabels[contact.contactType] ?? contact.contactType}</span>
                  <strong>{contact.contactValue}</strong>
                </div>
              ))
            )}
          </div>
          {targetNote && (
            <div className="wx-pr-contact-row">
              <span>{t.targetGroupLabel}</span>
              <strong>{targetNote}</strong>
            </div>
          )}
          {card.noteToWorker && (
            <div className="wx-pr-contact-row">
              <span>{t.agentNoteLabel}</span>
              <strong>{card.noteToWorker}</strong>
            </div>
          )}
          <div className="wx-pr-status-note">{t.statusNote}</div>
        </aside>
        <section>
          <div className="wx-pr-benefit-message">{t.benefitMessage(card.appName)}</div>
          <div className="wx-pr-benefits">
            {t.benefits.map((benefit) => (
              <BenefitCard key={benefit.title} icon={benefit.icon} title={benefit.title} desc={benefit.desc} />
            ))}
          </div>
        </section>
      </div>

      {onStartQuickRegister && (
        <div className="wx-pr-letter-cta-bar">
          <button type="button" className="wx-pr-submit-button wx-pr-letter-cta-button" onClick={onStartQuickRegister}>
            {t.startQuickRegister}
          </button>
          <p className="wx-pr-letter-cta-note">{t.letterCtaNote}</p>
        </div>
      )}
    </section>
  );
}

function BenefitCard({ icon, title, desc }) {
  return (
    <div className="wx-pr-benefit">
      <span className="wx-pr-benefit-icon" aria-hidden="true">
        {icon}
      </span>
      <div>
        <span className="wx-pr-benefit-title">{title}</span>
        <span className="wx-pr-benefit-desc">{desc}</span>
      </div>
    </div>
  );
}

/**
 * Prompt 133: the compact letter summary shown above the Quick Register
 * form (`view === "form"`) - just enough for the worker to confirm which
 * invite they are using, plus the way back to the full letter. Never
 * duplicates the full letter body/benefits list.
 */
function FormSummaryBar({ card, t, onBack }) {
  const targetNote = [card.intendedCountryCode, card.intendedLanguageCode].filter(Boolean).join(" / ");

  return (
    <div className="wx-pr-form-summary">
      <div className="wx-pr-form-summary-info">
        <span className="wx-pr-form-summary-label">{t.formSummaryLabel}</span>
        <strong>
          {card.agentDisplayName} · {card.agentCode}
        </strong>
        {targetNote && <span className="wx-pr-form-summary-target">{targetNote}</span>}
      </div>
      <button type="button" className="wx-pr-form-summary-back" onClick={onBack}>
        {t.backToLetter}
      </button>
    </div>
  );
}

/**
 * Prompt 139B: the in-page live-camera capture flow, shown only when
 * isCameraCaptureSupported() is true (real getUserMedia + secure context).
 * Deliberately not rendered as a dead button when unsupported - the parent
 * (PassportPhotoField) swaps this out for a calm fallback note instead of
 * mounting this component at all in that case, so there is never a button
 * here that does nothing when clicked.
 *
 * Prompt 139B-R2: adds a review step - "ถ่ายรูป" only captures a frame into
 * a preview, it does not finalize the selection. The worker then either
 * confirms with "ใช้รูปนี้" (calls onConfirm, closing the camera) or
 * discards with "ถ่ายใหม่" (back to the live view, stream stays open) -
 * matching the same "review before commit" pattern the file picker already
 * gets for free from the OS's own file dialog. "ปิดกล้อง" exits the camera
 * entirely at any point (live or review), always stopping the media stream.
 *
 * Capture works by drawing the live <video> frame onto an offscreen
 * <canvas> and reading it back as a real image/jpeg Blob (canvas.toBlob) -
 * a real capture, not a placeholder. If the browser's canvas 2D context is
 * unavailable for any reason, capture silently no-ops rather than throwing,
 * since this is a UX enhancement layered on top of the always-present file
 * picker (which still works either way).
 */
function CameraPanel({ t, onConfirm, onClose }) {
  const [status, setStatus] = useState("opening"); // "opening" | "live" | "review" | "error"
  const [reviewUrl, setReviewUrl] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const capturedFileRef = useRef(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const revokeReviewUrl = useCallback(() => {
    if (reviewUrl) {
      try {
        URL.revokeObjectURL(reviewUrl);
      } catch {
        // Nothing to do - the browser reclaims it on navigation anyway.
      }
    }
  }, [reviewUrl]);

  // Opens the camera exactly once on mount - CameraPanel is only ever
  // mounted while the worker has chosen the camera option, and unmounted
  // (see PassportPhotoField below) the moment they close it or confirm a
  // photo, so there is no separate "idle" state to manage here.
  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        setStatus("live");
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once on mount only
  }, []);

  useEffect(() => {
    if (status !== "live" || !videoRef.current || !streamRef.current) {
      return;
    }
    try {
      videoRef.current.srcObject = streamRef.current;
      const maybePromise = videoRef.current.play?.();
      if (maybePromise && typeof maybePromise.catch === "function") {
        maybePromise.catch(() => {});
      }
    } catch {
      // Some environments don't support programmatic play() the same way -
      // the live <video> element still renders regardless.
    }
  }, [status]);

  function handleCapture() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }
        const capturedFile = new File([blob], `passport-camera-${Date.now()}.jpg`, { type: "image/jpeg" });
        capturedFileRef.current = capturedFile;
        try {
          setReviewUrl(URL.createObjectURL(capturedFile));
        } catch {
          setReviewUrl(null);
        }
        setStatus("review");
      },
      "image/jpeg",
      0.9
    );
  }

  function handleRetake() {
    revokeReviewUrl();
    setReviewUrl(null);
    capturedFileRef.current = null;
    setStatus("live");
  }

  function handleUsePhoto() {
    if (capturedFileRef.current) {
      onConfirm(capturedFileRef.current);
    }
  }

  function handleClose() {
    revokeReviewUrl();
    onClose();
  }

  if (status === "opening") {
    return <p className="wx-pr-camera-status">{t.cameraOpeningText}</p>;
  }

  if (status === "error") {
    return (
      <div className="wx-pr-camera-error-wrap">
        <p className="wx-pr-camera-unavailable-note" role="alert">
          {t.cameraUnavailableNote}
        </p>
        <button type="button" className="wx-pr-camera-close-btn" onClick={handleClose}>
          {t.cameraCloseButton}
        </button>
      </div>
    );
  }

  if (status === "review") {
    return (
      <div className="wx-pr-camera-review">
        {reviewUrl && <img src={reviewUrl} alt="" className="wx-pr-camera-review-image" />}
        <div className="wx-pr-camera-live-actions">
          <button type="button" className="wx-pr-camera-capture-btn" onClick={handleUsePhoto}>
            {t.cameraUsePhotoButton}
          </button>
          <button type="button" className="wx-pr-camera-cancel-btn" onClick={handleRetake}>
            {t.cameraRetakeButton}
          </button>
        </div>
        <button type="button" className="wx-pr-camera-close-btn" onClick={handleClose}>
          {t.cameraCloseButton}
        </button>
      </div>
    );
  }

  return (
    <div className="wx-pr-camera-live">
      <video ref={videoRef} className="wx-pr-camera-video" autoPlay playsInline muted />
      <div className="wx-pr-camera-live-actions">
        <button type="button" className="wx-pr-camera-capture-btn" onClick={handleCapture}>
          {t.cameraCaptureCta}
        </button>
        <button type="button" className="wx-pr-camera-cancel-btn" onClick={handleClose}>
          {t.cameraCloseButton}
        </button>
      </div>
    </div>
  );
}

/**
 * Prompt 139B, redesigned by 139B-R2: the real passport/identity document
 * field. The chosen File (whichever path produced it) is sent with the
 * application on submit (see handleSubmit in PublicInviteRegisterPage
 * below), never faked as "sent" without actually being attached.
 *
 * Two clearly separate, equally-sized choices when no file is selected yet:
 * 1. "ถ่ายรูปด้วยกล้อง" (primary) - only rendered as a real button when
 *    isCameraCaptureSupported() is true; opens CameraPanel above. When
 *    unsupported (insecure context - e.g. this app's own http:// test
 *    deployment - or no getUserMedia at all), this slot shows the calm
 *    fallback note instead of a button that would do nothing.
 * 2. "อัปโหลดจากเครื่อง" (secondary) - always present, opens the hidden
 *    file input. Its accept/capture attributes still carry the
 *    image/jpeg,image/png,image/webp allow-list plus capture="environment",
 *    which many mobile browsers honor as a camera hint even without any JS
 *    camera API - a second, OS-level path to a camera photo that works
 *    independently of getUserMedia support.
 */
function PassportPhotoField({ file, onSelect, onRemove, t }) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const inputRef = useRef(null);
  const previewUrl = usePreviewUrl(file);
  const cameraSupported = isCameraCaptureSupported();

  function openPicker() {
    inputRef.current?.click();
  }

  function handleChange(event) {
    const selected = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    onSelect(selected);
    // Reset so choosing the same file again after Remove still fires onChange.
    event.target.value = "";
  }

  function handleCameraConfirm(capturedFile) {
    onSelect(capturedFile);
    setCameraOpen(false);
  }

  return (
    <div className="wx-pr-passport-box">
      <input
        ref={inputRef}
        type="file"
        accept={PASSPORT_ACCEPT}
        capture="environment"
        className="wx-visually-hidden"
        onChange={handleChange}
        aria-label={t.passportInputAriaLabel}
      />
      {file ? (
        <div className="wx-pr-passport-selected">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="wx-pr-passport-thumb" />
          ) : (
            <span className="wx-pr-passport-file-icon" aria-hidden="true" />
          )}
          <div className="wx-pr-passport-selected-info">
            <span className="wx-pr-passport-filename" title={file.name}>
              {file.name}
            </span>
            <span className="wx-pr-passport-filesize">
              {t.passportFileSizeLabel}: {formatFileSize(file.size)}
            </span>
          </div>
          <div className="wx-pr-passport-selected-actions">
            <button type="button" className="wx-pr-passport-action" onClick={openPicker}>
              {t.passportChange}
            </button>
            <button type="button" className="wx-pr-passport-action wx-pr-passport-action--remove" onClick={onRemove}>
              {t.passportRemove}
            </button>
          </div>
        </div>
      ) : cameraOpen ? (
        <CameraPanel t={t} onConfirm={handleCameraConfirm} onClose={() => setCameraOpen(false)} />
      ) : (
        <div className="wx-pr-photo-choice">
          {cameraSupported ? (
            <button
              type="button"
              className="wx-pr-photo-choice-btn wx-pr-photo-choice-btn--primary"
              onClick={() => setCameraOpen(true)}
            >
              <span className="wx-pr-photo-choice-icon wx-pr-photo-choice-icon--camera" aria-hidden="true" />
              <span>{t.passportChoiceCameraButton}</span>
            </button>
          ) : (
            <p className="wx-pr-camera-unavailable-note">{t.cameraUnavailableNote}</p>
          )}
          <button type="button" className="wx-pr-photo-choice-btn wx-pr-photo-choice-btn--secondary" onClick={openPicker}>
            <span className="wx-pr-photo-choice-icon wx-pr-photo-choice-icon--upload" aria-hidden="true" />
            <span>{t.passportChoiceUploadButton}</span>
          </button>
        </div>
      )}
      <p className="wx-pr-passport-note">{t.passportRequiredNote}</p>
    </div>
  );
}

function FormPanel({
  card,
  t,
  fullNameNative,
  phoneNumber,
  nationality,
  primarySkillCode,
  primarySkillOtherText,
  passportNumber,
  passportFile,
  onFullNameNativeChange,
  onPhoneNumberChange,
  onNationalityChange,
  onPrimarySkillCodeChange,
  onPrimarySkillOtherTextChange,
  onPassportNumberChange,
  onPassportFileChange,
  formErrors,
  submitting,
  submitError,
  onSubmit,
  callHref,
}) {
  const targetNote = [card.intendedCountryCode, card.intendedLanguageCode].filter(Boolean).join(" / ");

  return (
    <aside className="wx-pr-form-panel">
      <form onSubmit={onSubmit} noValidate>
        <div className="wx-pr-form-head">
          <div className="wx-pr-step">
            <span className="wx-pr-step-mark" aria-hidden="true">
              1
            </span>
            <b>{t.formStepLabel}</b>
          </div>
          <h2>{t.formHeading}</h2>
          <p>{t.formSubheading}</p>
          {targetNote && (
            <p>
              {t.formTargetPrefix} {targetNote}
            </p>
          )}
        </div>
        <div className="wx-pr-form-body">
          <label className="wx-pr-field">
            <span>{t.fieldFullName}</span>
            <input
              type="text"
              placeholder={t.fieldFullNamePlaceholder}
              value={fullNameNative}
              onChange={(event) => onFullNameNativeChange(event.target.value)}
              required
            />
          </label>
          <label className="wx-pr-field">
            <span>{t.fieldPhone}</span>
            <input
              type="tel"
              placeholder={t.fieldPhonePlaceholder}
              value={phoneNumber}
              onChange={(event) => onPhoneNumberChange(event.target.value)}
              required
            />
          </label>
          <label className="wx-pr-field">
            <span>{t.fieldCountry}</span>
            <select value={nationality} onChange={(event) => onNationalityChange(event.target.value)}>
              {NATIONALITY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label className="wx-pr-field">
            <span>{t.fieldSkill}</span>
            <select
              value={primarySkillCode}
              onChange={(event) => onPrimarySkillCodeChange(event.target.value)}
              aria-label={t.fieldSkill}
              required
            >
              <option value="" disabled>
                {t.fieldSkill}
              </option>
              {SKILL_CODES.map((code) => (
                <option key={code} value={code}>
                  {t.skillLabels[code]}
                </option>
              ))}
            </select>
          </label>
          {primarySkillCode === SKILL_OTHER_CODE && (
            <label className="wx-pr-field">
              <span>{t.skillLabels.Other}</span>
              <input
                type="text"
                placeholder={t.fieldSkillOtherPlaceholder}
                value={primarySkillOtherText}
                onChange={(event) => onPrimarySkillOtherTextChange(event.target.value)}
                required
              />
            </label>
          )}

          <label className="wx-pr-field">
            <span>{t.fieldPassportNumber}</span>
            <input
              type="text"
              placeholder={t.fieldPassportNumberPlaceholder}
              value={passportNumber}
              onChange={(event) => onPassportNumberChange(event.target.value)}
              required
            />
          </label>

          <label className="wx-pr-field">
            <span>{t.fieldPassport}</span>
            <PassportPhotoField file={passportFile} onSelect={onPassportFileChange} onRemove={() => onPassportFileChange(null)} t={t} />
          </label>

          {formErrors.length > 0 && (
            <p className="wx-pr-field-error" role="alert">
              {formErrors.join(" ")}
            </p>
          )}

          <div className="wx-pr-help-note">{t.helpNote(card.appName)}</div>
        </div>
        <div className="wx-pr-form-actions">
          {submitError && (
            <p className="wx-pr-submit-error" role="alert">
              {submitError}
            </p>
          )}
          <button type="submit" className="wx-pr-submit-button" disabled={submitting}>
            {submitting ? t.submitButtonSubmitting : t.submitButton(card.agentDisplayName)}
          </button>
          {callHref && (
            <a className="wx-pr-secondary-button" href={callHref}>
              {t.secondaryCallButton}
            </a>
          )}
        </div>
      </form>
    </aside>
  );
}

/**
 * Prompt 139B: `submittedSummary` is local UI state captured at the moment
 * of a successful submit (see handleSubmit below) - it is NOT re-derived
 * from the backend response (SubmitWorkerApplicationResponse has no
 * skill/passport/document fields at all, by design - see
 * Models/Public/PublicInviteDtos.cs). This is not fake data: it reflects
 * exactly what THIS same request just successfully sent, confirmed by the
 * 200 response. The full passport number is never part of this summary or
 * stored anywhere after submit - only booleans ("was a passport number
 * sent," never the value itself).
 */
function SuccessPanel({ response, submittedSummary, t }) {
  return (
    <div className="wx-pr-state-card">
      <span className="wx-pr-success-badge">{response.status}</span>
      <h1 className="wx-pr-state-title">{response.isDuplicate ? t.successDuplicateTitle : t.successTitle}</h1>
      <p className="wx-pr-state-body">{t.successAgentWillContact(response.agentDisplayName)}</p>
      <p className="wx-pr-state-body">{response.message}</p>

      {submittedSummary && (
        <ul className="wx-pr-success-summary">
          {submittedSummary.hasSkill && (
            <li>
              <CheckIcon />
              <span>{t.successSkillLine}</span>
            </li>
          )}
          {submittedSummary.hasPassportNumber && (
            <li>
              <CheckIcon />
              <span>{t.successPassportLine}</span>
            </li>
          )}
          {submittedSummary.hasDocument && (
            <li>
              <CheckIcon />
              <span>{t.successDocumentLine}</span>
            </li>
          )}
        </ul>
      )}

      <div className="wx-pr-success-ekyc-note">{t.successEkycNote}</div>
    </div>
  );
}
